import React, { useState, useEffect, useMemo, useCallback } from 'react'

import moment from 'moment'
import PropTypes from 'prop-types'

import debounce from 'lodash/debounce'
import forEach from 'lodash/forEach'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import omit from 'lodash/omit'

import { Grid } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/DeleteOutlined'

import { Scope } from '@galo-monitor/forms'
import useGaloMonitorApi from '@galo-monitor/services/hooks/useGaloMonitorApi'
import { momentBackDateFormat } from '@galo-monitor/utils/dates'
import IconButton from '@galo-monitor/web-components/IconButton'
import InputDate from '@galo-monitor/web-components/InputDate'
import InputFloat from '@galo-monitor/web-components/InputFloat'
import InputSelect from '@galo-monitor/web-components/InputSelect'
import InputUnit from '@galo-monitor/web-components/InputUnit'

import { Flex, LinkText, SectionTitle } from '../../theme'
import {
  formatRawMaterialsArray,
  formatMachinesArray,
  MACHINE_INITIAL_VALUE,
  RAW_MATERIAL_INITIAL_VALUE
} from '../../utils/formatters'
import {
  handleOnAreaTotalChange,
  handleOnRecommendationPerHectareChange
} from '../../utils/handlers'
import {
  initialMachinesOptions,
  initialRawMaterialsOptions,
  initialResponsibleOptions
} from '../../utils/initialOptions'
import {
  handleFetchMachineOptions,
  handleFetchRawMaterialOptions,
  handleFetchResponsibleOptions
} from '../../utils/requests'
import CustomInputSearch from '../CustomInputSearch'

const DEBOUNCE_TIME_IN_MS = 500

const SeedingFields = ({ activity, formRef, loading, locale, stateTypeButtons, t, unitInitials }) => {
  const [searchMachineParams, setSearchMachineParams] = useState({ name: '' })
  const [searchRawMaterialParams, setSearchRawMaterialParams] = useState({ name: '' })
  const [searchResponsibleParams, setSearchResponsibleParams] = useState({ name: '' })
  const [rawMaterialInputs, setRawMaterialInputs] = useState({})
  const [machinesInputs, setMachinesInputs] = useState({})

  const {
    data: stocks,
    isValidating: isValidatingStocks
  } = useGaloMonitorApi('/farms/properties/activities/stocks')

  const stockOptions = useMemo(
    () => map(stocks, stock => ({
      label: stock.name,
      value: String(stock.id)
    })),
    [stocks]
  )

  const onRawMaterialKeyUp = debounce(
    value => {
      setSearchRawMaterialParams({
        name: value
      })
    },
    DEBOUNCE_TIME_IN_MS
  )

  const onMachineKeyUp = useCallback(debounce(
    value => {
      setSearchMachineParams({
        name: value
      })
    },
    DEBOUNCE_TIME_IN_MS
  ), [])

  const onResponsibleKeyUp = debounce(
    value => {
      setSearchResponsibleParams({
        name: value
      })
    },
    DEBOUNCE_TIME_IN_MS
  )

  const onUnitChange = (unit, index) =>
    setRawMaterialInputs(prevState => ({
      ...prevState,
      [index]: {
        ...prevState[index],
        unit
      }
    }))

  const onNewRawMaterial = () => {
    const currentIndex = Object.keys(rawMaterialInputs).pop()

    setRawMaterialInputs(prevState => ({
      ...prevState, [Number(currentIndex) + 1]: RAW_MATERIAL_INITIAL_VALUE()
    }))
  }

  const onRemoveRawMaterial = index =>
    setRawMaterialInputs(prevState => omit(prevState, [index]))

  const onNewMachine = () => {
    const currentIndex = Object.keys(machinesInputs).pop()

    setMachinesInputs(prevState => ({
      ...prevState, [Number(currentIndex) + 1]: MACHINE_INITIAL_VALUE()
    }))
  }

  const onRemoveMachine = index =>
    setMachinesInputs(prevState => omit(prevState, [index]))

  useEffect(() => {
    if (!isEmpty(activity.agriculturalInputs)) {
      const realizedAgriculturalInputs = []
      const plannedAgriculturalInputs = []

      forEach(activity.agriculturalInputs, rawMaterial => {
        if (rawMaterial.type === 'realized') {
          realizedAgriculturalInputs.push(rawMaterial)
          return
        }

        plannedAgriculturalInputs.push(rawMaterial)
      })

      setRawMaterialInputs(
        formatRawMaterialsArray(
          isEmpty(realizedAgriculturalInputs)
            ? plannedAgriculturalInputs
            : realizedAgriculturalInputs
        )
      )
    } else if(!loading) {
      setRawMaterialInputs({ 0: RAW_MATERIAL_INITIAL_VALUE() })
    }
  }, [activity.agriculturalInputs, loading])

  useEffect(() => {
    if (!isEmpty(activity.machines)) {
      const realizedMachines = []
      const plannedMachines = []

      forEach(activity.machines, rawMaterial => {
        if (rawMaterial.type === 'realized') {
          realizedMachines.push(rawMaterial)
          return
        }

        plannedMachines.push(rawMaterial)
      })

      setMachinesInputs(
        formatMachinesArray(
          isEmpty(realizedMachines)
            ? plannedMachines
            : realizedMachines
        )
      )
    } else if(!loading) {
      setMachinesInputs({ 0: MACHINE_INITIAL_VALUE() })
    }
  }, [activity.machines, loading])

  return (
    <Grid container spacing={ 3 } style={ { margin: 0 } }>
      <Grid item xs={ 12 }>
        <SectionTitle variant="h6">
          {t('about the operation')}
        </SectionTitle>
      </Grid>
      <Grid item xs={ 12 }>
        {stateTypeButtons}
      </Grid>
      <Grid item xs={ 12 } sm={ 5 }>
        <InputDate
          name="date_initial"
          label={ t('start date') }
          defaultValue={
            moment(activity.dateInitialRealized ?? activity.dateInitialPlanned)
              .format(momentBackDateFormat)
          }
          disabled={ loading }
          locale={ locale }
          fullWidth
        />
      </Grid>
      <Grid item xs={ 12 } sm={ 5 }>
        <InputDate
          name="date_finish"
          label={ t('end date') }
          defaultValue={
            moment(activity.dateFinishRealized ?? activity.dateFinishPlanned)
              .format(momentBackDateFormat)
          }
          disabled={ loading }
          locale={ locale }
          fullWidth
        />
      </Grid>
      <Grid item xs={ 12 } sm>
        <InputUnit
          name="area_total"
          label={ t('total area') }
          defaultValue={ activity.areaTotalRealized?.toString() ?? activity.areaTotalPlanned?.toString() ?? '' }
          disabled={ loading }
          onFieldValueChange={ value => handleOnAreaTotalChange({ value, formRef }) }
          unit={ unitInitials }
          fullWidth
        />
      </Grid>
      <Grid item xs={ 12 }>
        <SectionTitle variant="h6">
          {t('raw material', { howMany: 1 })}
        </SectionTitle>
      </Grid>
      {map(rawMaterialInputs, ({ key, unit, ...rawMaterial }, index) => (
        <Scope path={ `agricultural_inputs[${ index }]` } key={ key }>
          <Grid item xs={ 12 } sm={ 5 }>
            <CustomInputSearch
              name="agricultural_input_id"
              label={ t('raw material', { howMany: 1 }) }
              onKeyUp={ evt => onRawMaterialKeyUp(evt.target.value) }
              options={ handleFetchRawMaterialOptions }
              queryParams={ searchRawMaterialParams }
              initialOptions={ isEmpty(activity) ? [] : initialRawMaterialsOptions(activity.agriculturalInputs) }
              defaultValue={ rawMaterial.agriculturalInputStockId?.toString() }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 1 }>
            <InputSelect
              name="unit"
              label={ t('unit', { howMany: 1 }) }
              onChange={ evt => onUnitChange(evt.target.value, index) }
              options={ [
                { label: 'Kg', value: 'Kg' },
                { label: 'Lt', value: 'Lt' }
              ] }
              defaultValue={ unit }
              disabled={ loading }
              disableClearable
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm>
            <InputFloat
              name="recommendation_per_hectare"
              label={ t(`recommendation per ${ unitInitials }`) }
              defaultValue={ rawMaterial.recommendationPerHectare?.toString() }
              disabled={ loading }
              onFieldValueChange={ value => handleOnRecommendationPerHectareChange({
                value,
                formRef,
                fieldName: `agricultural_inputs[${ index }].quant_total`,
                loading
              }) }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm>
            <InputUnit
              name="quant_total"
              label={ t('total quantity') }
              unit={ unit }
              defaultValue={ rawMaterial.quantTotal?.toString() }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 12 } sm={ 1 }>
            <Flex>
              <IconButton
                size="small"
                disabled={ Number(index) === 0 }
                onClick={ () => onRemoveRawMaterial(index) }
              >
                <DeleteIcon color="action" />
              </IconButton>
            </Flex>
          </Grid>
        </Scope>
      ))}
      <Grid item xs={ 12 } style={ { marginBottom: 10 } }>
        <LinkText onClick={ onNewRawMaterial }>
          {t('add another')}
        </LinkText>
      </Grid>
      <Grid item xs={ 12 }>
        <SectionTitle variant="h6">
          {t('stock', { howMany: 1 })}
        </SectionTitle>
      </Grid>
      <Grid item xs={ 12 }>
        <InputSelect
          name="stock_id"
          label={ t('select raw material source stock') }
          disabled={ loading || isValidatingStocks }
          loading={ isValidatingStocks }
          options={ stockOptions }
          defaultValue={
            activity.stockIdPlanned?.toString() ??
            activity.stockIdRealized?.toString() ??
            ''
          }
          fullWidth
        />
      </Grid>
      <Grid item xs={ 12 }>
        <SectionTitle variant="h6">
          {t('machines and implements')}
        </SectionTitle>
      </Grid>
      {map(machinesInputs, (machine, index) => (
        <Scope path={ `machines[${ index }]` } key={ index }>
          <Grid item xs={ 11 } sm={ 11 }>
            <CustomInputSearch
              name="machine_id"
              label={ t('machine', { howMany: 1 }) }
              onKeyUp={ evt => onMachineKeyUp(evt.target.value) }
              options={ handleFetchMachineOptions }
              queryParams={ searchMachineParams }
              initialOptions={ isEmpty(activity) ? [] : initialMachinesOptions(activity.machines) }
              defaultValue={ machine.id?.toString() ?? '' }
              disabled={ loading }
              fullWidth
            />
          </Grid>
          <Grid item xs={ 1 } sm={ 1 }>
            <Flex>
              <IconButton
                size="small"
                disabled={ Number(index) === 0 }
                onClick={ () => onRemoveMachine(index) }
              >
                <DeleteIcon color="action" />
              </IconButton>
            </Flex>
          </Grid>
        </Scope>
      ))}
      <Grid item xs={ 12 } style={ { marginBottom: 10 } }>
        <LinkText onClick={ onNewMachine }>
          {t('add another')}
        </LinkText>
      </Grid>
      <Grid item xs={ 12 }>
        <SectionTitle variant="h6">
          {t('responsible')}
        </SectionTitle>
      </Grid>
      <Grid item xs={ 12 }>
        <CustomInputSearch
          name="user_responsible_id"
          label={ t('collaborator', { howMany: 1 }) }
          onKeyUp={ evt => onResponsibleKeyUp(evt.target.value) }
          options={ handleFetchResponsibleOptions }
          queryParams={ searchResponsibleParams }
          initialOptions={ isEmpty(activity) ? [] : initialResponsibleOptions(activity) }
          defaultValue={
            activity.userResponsibleIdRealized?.toString() ??
            activity.userResponsibleIdPlanned?.toString() ??
            ''
          }
          disabled={ loading }
          fullWidth
        />
      </Grid>
    </Grid>
  )
}

SeedingFields.propTypes = {
  activity: PropTypes.object,
  formRef: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  locale: PropTypes.string.isRequired,
  stateTypeButtons: PropTypes.oneOfType([PropTypes.element, PropTypes.object]),
  t: PropTypes.func.isRequired,
  unitInitials: PropTypes.string.isRequired
}

SeedingFields.defaultProps = {
  activity: {},
  stateTypeButtons: <></>
}

export default SeedingFields
